import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { Users, Calendar, UserCheck, HardDrive } from 'lucide-react';
import { organizationService } from '../../services';
import PlanValidityCard from '../../components/billing/PlanValidityCard';

/**
 * Organization Detail Page (Super Admin)
 * View detailed information about a specific organization
 */

const OrganizationDetailPage = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, usage, billing, activity
  const [suspendModal, setSuspendModal] = useState({ isOpen: false, reason: '', loading: false });
  const [reactivateModal, setReactivateModal] = useState({ isOpen: false, loading: false });

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await organizationService.getOrganization(organizationId);
        const org = response.data || response;

        setOrganization({
          id: org._id,
          name: org.name || '',
          plan: org.plan || 'free',
          // Drives whether Change Plan is offered — individual orgs only.
          type: org.type || 'standard',
          status: org.status || 'active',
          suspensionReason: org.suspensionReason || '',
          suspendedAt: org.suspendedAt || '',
          trainers: org.trainersCount || 0,
          sessions: org.sessionsCount || 0,
          totalParticipants: org.totalParticipants || 0,
          storageUsed: org.storageUsed || 0,
          storageLimit: org.storageLimit || 1024,
          createdAt: org.createdAt || new Date().toISOString(),
          contactEmail: org.contactEmail || org.email || '',
          contactName: org.contactName || org.contactPerson || '',
          contactPhone: org.phone || '',
          renewalDate: org.subscription?.renewalDate || org.renewalDate || null,
          autoRenew: org.subscription?.autoRenew || org.autoRenew || false,
          billingAddress: org.address
            ? [
                org.address.street,
                org.address.city,
                org.address.state,
                org.address.zipCode,
                org.address.country
              ].filter(Boolean).join(', ') || 'Not provided'
            : 'Not provided',
          limits: org.limits || {
            maxSessions: 10,
            maxParticipantsPerSession: 100,
            maxTrainers: 10,
            maxStorageMB: 1024,
          },
          usage: {
            sessions: {
              used: org.sessionsCount || 0,
              limit: org.limits?.maxSessions || 50,
            },
            trainers: {
              used: org.trainersCount || 0,
              limit: org.limits?.maxTrainers || 10,
            },
            storage: {
              used: org.storageUsed || 0,
              limit: org.limits?.maxStorageMB || 1024,
            },
          },
          billing: org.billing || [],
          activity: org.activity || [],
          // Canonical Subscription doc returned by the backend (or a
          // synthesized record for orgs that never had one). Drives the
          // Plan Validity card in the Billing tab.
          subscriptionRecord: org.subscriptionRecord || null,
          historicalUsage: org.historicalUsage || {
            sessionsLast30Days: 0,
            sessionsChange: 0,
            participantsLast30Days: 0,
            participantsChange: 0,
            engagementLast30Days: 0,
            engagementChange: 0,
          },
        });
      } catch (error) {
        console.error('Error fetching organization:', error);
        toast.error('Failed to load organization details');
      }
    };

    fetchOrganization();
  }, [organizationId]);

  if (!organization) {
    return (
      <DashboardLayout title="Organization Details">
        <div className="p-6 flex items-center justify-center">
          <p className="text-neutral-500">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'contact', label: 'Contact Information' },
    { id: 'usage', label: 'Usage Statistics' },
    { id: 'billing', label: 'Billing History' },
    { id: 'activity', label: 'Activity Timeline' },
  ];

  const getPlanBadgeVariant = (plan) => {
    const planLower = plan?.toLowerCase() || '';
    switch (planLower) {
      case 'free':
        return 'default';
      case 'pro':
        return 'primary';
      case 'pro_plus':
        return 'info';
      case 'enterprise_starter':
        return 'warning';
      case 'enterprise_scale':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatPlanName = (plan) => {
    const planNames = {
      free: 'Free',
      pro: 'Pro',
      pro_plus: 'Pro Plus',
      enterprise_starter: 'Enterprise Starter',
      enterprise_scale: 'Enterprise Scale',
    };
    return planNames[plan?.toLowerCase()] || plan;
  };

  const getPlanPrice = (plan) => {
    const prices = {
      free: null,
      pro: '₹1,999/month',
      pro_plus: '₹5,999/month',
      enterprise_starter: '₹1,80,000/year',
      enterprise_scale: 'Custom',
    };
    return prices[plan?.toLowerCase()] || null;
  };

  // Canonical plan limits (source of truth, matching pricing page)
  const getPlanLimits = (plan) => {
    const limits = {
      free: { maxSessions: 3, maxParticipantsPerSession: 30, maxTrainers: 1, sessionsLabel: '3 total' },
      pro: { maxSessions: 20, maxParticipantsPerSession: 30, maxTrainers: 1, sessionsLabel: '20/month' },
      pro_plus: { maxSessions: -1, maxParticipantsPerSession: 100, maxTrainers: 3, sessionsLabel: 'Unlimited' },
      enterprise_starter: { maxSessions: -1, maxParticipantsPerSession: 200, maxTrainers: 5, sessionsLabel: 'Unlimited' },
      enterprise_scale: { maxSessions: -1, maxParticipantsPerSession: -1, maxTrainers: -1, sessionsLabel: 'Unlimited' },
    };
    return limits[plan?.toLowerCase()] || limits.free;
  };

  const openSuspendModal = () => {
    setSuspendModal({ isOpen: true, reason: '', loading: false });
  };

  const closeSuspendModal = () => {
    setSuspendModal({ isOpen: false, reason: '', loading: false });
  };

  const handleSuspendConfirm = async () => {
    if (!suspendModal.reason.trim()) {
      toast.error('Please provide a reason for suspension');
      return;
    }

    setSuspendModal((prev) => ({ ...prev, loading: true }));
    try {
      await organizationService.suspendOrganization(organizationId, suspendModal.reason.trim());
      setOrganization((prev) => ({
        ...prev,
        status: 'suspended',
        suspensionReason: suspendModal.reason.trim(),
        suspendedAt: new Date().toISOString(),
      }));
      toast.success('Organization suspended successfully');
      closeSuspendModal();
    } catch (error) {
      toast.error(error.message || 'Failed to suspend organization');
      setSuspendModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openReactivateModal = () => {
    setReactivateModal({ isOpen: true, loading: false });
  };

  const handleReactivateConfirm = async () => {
    setReactivateModal((prev) => ({ ...prev, loading: true }));
    try {
      await organizationService.reactivateOrganization(organizationId);
      setOrganization((prev) => ({
        ...prev,
        status: 'active',
        suspensionReason: '',
        suspendedAt: '',
      }));
      toast.success('Organization reactivated successfully');
      setReactivateModal({ isOpen: false, loading: false });
    } catch (error) {
      toast.error(error.message || 'Failed to reactivate organization');
      setReactivateModal((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <DashboardLayout title="Organization Details">
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 pb-20 sm:pb-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group"
        >
          <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-600 rounded-xl flex items-center justify-center text-white text-xl sm:text-2xl font-bold flex-shrink-0">
              {organization.name.charAt(0)}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <h1 className="text-xl sm:text-3xl font-bold text-neutral-900">{organization.name}</h1>
                <Badge variant={getPlanBadgeVariant(organization.plan)}>{formatPlanName(organization.plan)} Plan</Badge>
                {(() => {
                  // Priority: suspended > expired > org.status. Suspension blocks
                  // login (more severe), so it wins. Expiry is derived from the
                  // subscription record so the header reflects effective state,
                  // not the stale org.status field that ignores plan validity.
                  const subEnd = organization.subscriptionRecord?.currentPeriodEnd;
                  const subStatus = organization.subscriptionRecord?.status;
                  const isExpired =
                    subStatus === 'expired' ||
                    subStatus === 'past_due' ||
                    (subEnd && new Date(subEnd).getTime() < Date.now());
                  if (organization.status === 'suspended') {
                    return <Badge variant="error">suspended</Badge>;
                  }
                  if (isExpired) {
                    return <Badge variant="error">Expired</Badge>;
                  }
                  return (
                    <Badge variant={organization.status === 'active' ? 'live' : 'default'}>
                      {organization.status}
                    </Badge>
                  );
                })()}
              </div>
              <p className="text-xs sm:text-sm text-neutral-600">Member since {new Date(organization.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/admin/organizations')}>
              Back to List
            </Button>
          </div>
        </div>

        {/* Suspension Banner */}
        {organization.status === 'suspended' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">This organization is suspended</p>
              {organization.suspensionReason && (
                <p className="text-sm text-red-700 mt-1">Reason: {organization.suspensionReason}</p>
              )}
              {organization.suspendedAt && (
                <p className="text-xs text-red-600 mt-1">
                  Suspended on {new Date(organization.suspendedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
            </div>
            <Button variant="primary" size="sm" onClick={openReactivateModal}>
              Reactivate
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-neutral-200 overflow-x-auto">
          <div className="flex gap-4 sm:gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id ? 'text-primary-600' : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"></div>}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Contact & Subscription Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card title="Organization Details" padding="lg">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Organization Name</p>
                    <p className="text-neutral-900">{organization.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Website</p>
                    {organization.website ? (
                      <a
                        href={organization.website.startsWith('http') ? organization.website : `https://${organization.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {organization.website}
                      </a>
                    ) : (
                      <p className="text-neutral-500">Not provided</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Address</p>
                    <p className="text-neutral-900 text-sm">{organization.billingAddress || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Contact Email</p>
                    <p className="text-neutral-900">{organization.contactEmail || 'Not provided'}</p>
                  </div>
                </div>
              </Card>

              <Card title="Subscription Details" padding="lg">
                <div className="space-y-3">
                  <div>
                    {(() => {
                      // When the plan has lapsed (subscription.status flagged
                      // expired/past_due, or currentPeriodEnd in the past), the
                      // org isn't actively on this plan anymore — it's the last
                      // plan they had. Label honestly.
                      const subEnd = organization.subscriptionRecord?.currentPeriodEnd;
                      const subStatus = organization.subscriptionRecord?.status;
                      const isExpired =
                        subStatus === 'expired' ||
                        subStatus === 'past_due' ||
                        (subEnd && new Date(subEnd).getTime() < Date.now());
                      return (
                        <p className="text-sm font-medium text-neutral-600">
                          {isExpired ? 'Previous Plan' : 'Current Plan'}
                        </p>
                      );
                    })()}
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={getPlanBadgeVariant(organization.plan)}>{formatPlanName(organization.plan)}</Badge>
                      {getPlanPrice(organization.plan) && (
                        <span className="text-neutral-900">
                          {getPlanPrice(organization.plan)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Plan Limits</p>
                    {(() => {
                      const limits = getPlanLimits(organization.plan);
                      return (
                        <div className="mt-1 space-y-1 text-sm text-neutral-900">
                          <p>Sessions: {limits.sessionsLabel}</p>
                          <p>Participants/session: {limits.maxParticipantsPerSession === -1 ? 'Unlimited' : limits.maxParticipantsPerSession}</p>
                          <p>Trainers: {limits.maxTrainers === -1 ? 'Unlimited' : limits.maxTrainers}</p>
                        </div>
                      );
                    })()}
                  </div>
                  {(() => {
                    // Prefer the canonical Subscription doc's currentPeriodEnd —
                    // that's the value Dodo's webhooks set verbatim from
                    // `next_billing_date`. Fall back to the org-level
                    // renewalDate placeholder only if the canonical record is
                    // missing (legacy orgs).
                    const renewalDate =
                      organization.subscriptionRecord?.currentPeriodEnd
                      || organization.renewalDate;
                    if (!renewalDate) return null;
                    return (
                      <div>
                        <p className="text-sm font-medium text-neutral-600">
                          {organization.plan === 'free' ? 'Plan End Date' : 'Renewal Date'}
                        </p>
                        <p className="text-neutral-900">
                          {new Date(renewalDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    );
                  })()}
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Auto-Renew</p>
                    <p className="text-neutral-900">{organization.autoRenew ? '✓ Enabled' : '✗ Disabled'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Account Status</p>
                    {(() => {
                      const subEnd = organization.subscriptionRecord?.currentPeriodEnd;
                      const subStatus = organization.subscriptionRecord?.status;
                      const isExpired =
                        subStatus === 'expired' ||
                        subStatus === 'past_due' ||
                        (subEnd && new Date(subEnd).getTime() < Date.now());
                      if (organization.status === 'suspended') {
                        return <Badge variant="error">Suspended</Badge>;
                      }
                      if (isExpired) {
                        return <Badge variant="error">Plan Expired</Badge>;
                      }
                      return <Badge variant="live">Active - Good Standing</Badge>;
                    })()}
                  </div>
                </div>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <StatCard label="Active Trainers"     value={organization.trainers}                       icon={Users}      variant="blue" />
              <StatCard label="Total Sessions"      value={organization.sessions}                       icon={Calendar}   variant="emerald" />
              <StatCard label="Participants Served" value={organization.totalParticipants.toLocaleString()} icon={UserCheck} variant="violet" />
              <StatCard label="Storage Used"        value={`${organization.storageUsed} MB`}            icon={HardDrive}  variant="amber" />
            </div>

            {/* Actions */}
            <Card title="Quick Actions" padding="lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button variant="ghost" fullWidth onClick={() => navigate(`/admin/sessions?org=${organizationId}&orgName=${encodeURIComponent(organization.name)}`)}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  View All Sessions
                </Button>
                <Button variant="ghost" fullWidth onClick={() => navigate(`/admin/users?org=${organizationId}&orgName=${encodeURIComponent(organization.name)}`)}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  View Trainers
                </Button>
                {organization.status === 'suspended' ? (
                  <Button variant="ghost" fullWidth onClick={openReactivateModal} className="text-green-600 hover:bg-green-50">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Reactivate Account
                  </Button>
                ) : (
                  <Button variant="ghost" fullWidth onClick={openSuspendModal} className="text-red-600 hover:bg-red-50">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Suspend Account
                  </Button>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Contact Information Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            <Card title="Contact Person Details (Client Details)" padding="lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Contact Name</p>
                    <p className="text-neutral-900 mt-1">{organization.contactName || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Email Address</p>
                    {organization.contactEmail ? (
                      <a
                        href={`mailto:${organization.contactEmail}`}
                        className="text-primary-600 hover:text-primary-700 hover:underline mt-1 inline-block"
                      >
                        {organization.contactEmail}
                      </a>
                    ) : (
                      <p className="text-neutral-500 mt-1">Not provided</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Phone Number</p>
                    {organization.contactPhone ? (
                      <a
                        href={`tel:${organization.contactPhone}`}
                        className="text-neutral-900 hover:text-primary-600 mt-1 inline-block"
                      >
                        {organization.contactPhone}
                      </a>
                    ) : (
                      <p className="text-neutral-500 mt-1">Not provided</p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Job Title</p>
                    <p className="text-neutral-900 mt-1">{organization.contactJobTitle || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Department</p>
                    <p className="text-neutral-900 mt-1">{organization.contactDepartment || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-600">Billing Address</p>
                    <p className="text-neutral-900 mt-1 text-sm">{organization.billingAddress || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Usage Statistics Tab */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            <Card title="Plan Limits & Usage" padding="lg">
              <div className="space-y-6">
                {/* Total Sessions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700">Total Sessions</span>
                    <span className="text-sm font-semibold text-neutral-900">
                      {organization.usage.sessions.limit === -1 || !organization.usage.sessions.limit
                        ? `${organization.usage.sessions.used} / Unlimited`
                        : `${organization.usage.sessions.used}/${organization.usage.sessions.limit}`}
                    </span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-3">
                    <div
                      className="bg-blue-500 h-3 rounded-full"
                      style={{
                        width: organization.usage.sessions.limit === -1 || !organization.usage.sessions.limit
                          ? '10%'
                          : `${Math.min((organization.usage.sessions.used / organization.usage.sessions.limit) * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                </div>

                {/* Trainers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700">Trainers</span>
                    <span className="text-sm font-semibold text-neutral-900">
                      {organization.usage.trainers.limit === -1 || !organization.usage.trainers.limit
                        ? `${organization.usage.trainers.used} / Unlimited`
                        : `${organization.usage.trainers.used}/${organization.usage.trainers.limit}`}
                    </span>
                  </div>
                  <div className="w-full bg-neutral-200 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full"
                      style={{
                        width: organization.usage.trainers.limit === -1 || !organization.usage.trainers.limit
                          ? '10%'
                          : `${Math.min((organization.usage.trainers.used / organization.usage.trainers.limit) * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                </div>

              </div>
            </Card>

            <Card title="Historical Usage (Last 30 Days)" padding="lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="p-3 sm:p-4 border border-neutral-200 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-neutral-600 mb-2">Sessions Conducted</p>
                  <p className="text-xl sm:text-3xl font-bold text-neutral-900">{organization.historicalUsage.sessionsLast30Days}</p>
                  <p className={`text-xs mt-1 ${organization.historicalUsage.sessionsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {organization.historicalUsage.sessionsChange >= 0 ? '+' : ''}{organization.historicalUsage.sessionsChange} from previous month
                  </p>
                </div>
                <div className="p-3 sm:p-4 border border-neutral-200 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-neutral-600 mb-2">Total Participants</p>
                  <p className="text-xl sm:text-3xl font-bold text-neutral-900">{organization.historicalUsage.participantsLast30Days.toLocaleString()}</p>
                  <p className={`text-xs mt-1 ${organization.historicalUsage.participantsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {organization.historicalUsage.participantsChange >= 0 ? '+' : ''}{organization.historicalUsage.participantsChange} from previous month
                  </p>
                </div>
                <div className="p-3 sm:p-4 border border-neutral-200 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-neutral-600 mb-2">Avg. Engagement</p>
                  <p className="text-xl sm:text-3xl font-bold text-neutral-900">{organization.historicalUsage.engagementLast30Days}%</p>
                  <p className={`text-xs mt-1 ${organization.historicalUsage.engagementChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {organization.historicalUsage.engagementChange >= 0 ? '+' : ''}{organization.historicalUsage.engagementChange}% from previous month
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Billing History Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-5">
            <PlanValidityCard
              context="organization"
              orgType={organization.type}
              organizationId={organization.id}
              subscriptionRecord={organization.subscriptionRecord}
              onExtended={({ currentPeriodEnd, status, extensions, currentPeriodStart }) => {
                setOrganization((prev) => prev ? ({
                  ...prev,
                  // Reactivation might have flipped status; surface that
                  // immediately on the page header / status badges.
                  renewalDate: currentPeriodEnd || prev.renewalDate,
                  subscriptionRecord: {
                    ...(prev.subscriptionRecord || {}),
                    currentPeriodStart: currentPeriodStart ?? prev.subscriptionRecord?.currentPeriodStart,
                    currentPeriodEnd: currentPeriodEnd ?? prev.subscriptionRecord?.currentPeriodEnd,
                    status: status ?? prev.subscriptionRecord?.status,
                    extensions: extensions ?? prev.subscriptionRecord?.extensions ?? [],
                    synthesized: false,
                  },
                }) : prev);
              }}
            />

            <Card title="Billing History" padding="lg">
            {organization.billing && organization.billing.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Description</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-neutral-700">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organization.billing.map((bill) => (
                      <tr key={bill.id} className="border-b border-neutral-100">
                        <td className="py-3 px-4 text-sm text-neutral-900">{bill.date}</td>
                        <td className="py-3 px-4 text-sm text-neutral-700">{bill.description}</td>
                        <td className="py-3 px-4 text-sm font-semibold text-neutral-900">₹{bill.amount.toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <Badge variant="live">Paid</Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="ghost" size="sm">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-neutral-600 font-medium mb-1">No billing history yet</p>
                <p className="text-sm text-neutral-500">Billing records will appear here once payments are made.</p>
              </div>
            )}
            </Card>
          </div>
        )}

        {/* Activity Timeline Tab */}
        {activeTab === 'activity' && (
          <Card title="Recent Activity" padding="lg">
            {organization.activity && organization.activity.length > 0 ? (
              <div className="space-y-4">
                {organization.activity.map((activity, idx) => {
                  const dotColor = {
                    organization: 'bg-orange-500',
                    user: 'bg-blue-500',
                    session: 'bg-green-500',
                    billing: 'bg-purple-500',
                    suspension: 'bg-red-500',
                  }[activity.type] || 'bg-neutral-400';
                  const typeLabel = {
                    organization: 'Organization',
                    user: 'User',
                    session: 'Session',
                    billing: 'Billing',
                    suspension: 'Suspension',
                  }[activity.type] || 'Activity';
                  // Backend gives us `action` ("Session created", "Trainer added"); fall
                  // back to the bare type label only if the row is missing it.
                  const heading = activity.action || typeLabel;
                  // Same story for the detail line — backend uses `details`, but tolerate
                  // an older `description` field if older records exist.
                  const detail = activity.details || activity.description || '';
                  const ts = activity.timestamp ? new Date(activity.timestamp) : null;
                  const formatted = ts
                    ? `${ts.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })} · ${ts.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
                    : '';
                  return (
                    <div key={activity.id || idx} className="flex items-start gap-4 p-4 border border-neutral-200 rounded-lg">
                      <div className={`w-2.5 h-2.5 ${dotColor} rounded-full mt-2 flex-shrink-0`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <p className="font-medium text-neutral-900">{heading}</p>
                          <span className="text-xs text-neutral-500 whitespace-nowrap">{formatted}</span>
                        </div>
                        {detail && <p className="text-sm text-neutral-600">{detail}</p>}
                        {activity.user && (
                          <p className="text-xs text-neutral-500 mt-1">by {activity.user}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-neutral-600 font-medium mb-1">No activity recorded yet</p>
                <p className="text-sm text-neutral-500">Activity timeline will show actions performed by this organization.</p>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Suspend Organization Modal */}
      <Modal
        isOpen={suspendModal.isOpen}
        onClose={closeSuspendModal}
        title="Suspend Organization"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={closeSuspendModal} disabled={suspendModal.loading}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleSuspendConfirm}
              disabled={!suspendModal.reason.trim() || suspendModal.loading}
            >
              {suspendModal.loading ? 'Suspending...' : 'Suspend Organization'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Warning */}
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-xl">
            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">
                You are about to suspend <strong>{organization?.name}</strong>
              </p>
              <p className="text-xs text-red-600 mt-1">
                All users in this organization will lose access immediately. They will see the suspension reason on their dashboard.
              </p>
            </div>
          </div>

          {/* Reason textarea */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              Reason for suspension <span className="text-red-500">*</span>
            </label>
            <textarea
              value={suspendModal.reason}
              onChange={(e) => setSuspendModal((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g., Violation of terms of service, Non-payment, etc."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2.5 border border-neutral-300 rounded-xl text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              autoFocus
            />
            <p className="text-xs text-neutral-400 mt-1 text-right">{suspendModal.reason.length}/500</p>
          </div>
        </div>
      </Modal>

      {/* Reactivate Organization Modal */}
      <Modal
        isOpen={reactivateModal.isOpen}
        onClose={() => setReactivateModal({ isOpen: false, loading: false })}
        title="Reactivate Organization"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setReactivateModal({ isOpen: false, loading: false })} disabled={reactivateModal.loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleReactivateConfirm}
              disabled={reactivateModal.loading}
            >
              {reactivateModal.loading ? 'Reactivating...' : 'Reactivate'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-100 rounded-xl">
            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-green-800">
                You are about to reactivate <strong>{organization?.name}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                All users in this organization will regain access to their accounts immediately.
              </p>
            </div>
          </div>

          {organization?.suspensionReason && (
            <div>
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">Previous suspension reason</p>
              <p className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-lg p-3">{organization.suspensionReason}</p>
            </div>
          )}
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default OrganizationDetailPage;
